import { HISTORY, MIN_COVERAGE } from './config';
import { daysBetween } from './dates';
import { clamp, decayPerDay, round6 } from './math';

export interface NutrientDay {
  /** 'YYYY-MM-DD'. */
  date: string;
  /** Solo los días marcados como completos entran (SPEC 7.5). */
  complete: boolean;
  /** Ingesta / objetivo, sin capar. `null` si no hay objetivo para ese nutriente. */
  ratio: number | null;
  /** Fracción de las kcal del día en que ese nutriente era conocido. */
  coverage: number;
}

export type HistoryStatus = 'silencio' | 'datos_insuficientes' | 'alerta' | 'vigilar';

export interface HistoryResult {
  status: HistoryStatus;
  /** Media ponderada exponencial, semivida de 14 días. */
  ema: number;
  /** Media simple de los últimos 7 días válidos. */
  last7: number;
  /** Media simple de los últimos 30 días válidos. */
  last30: number;
  validDays: number;
}

/**
 * Ponderación histórica de un nutriente (SPEC 7.5).
 *
 * El objetivo de estas reglas es no dar la brasa. Tres ideas:
 *
 *  - **Silencio** manda sobre todo lo demás: si el último mes va bien, da igual
 *    lo malo que fuera el historial. Recordarle a alguien un fallo que ya
 *    corrigió solo consigue que desinstale la app.
 *  - **Alerta** exige que fallen DOS medidas a la vez (la exponencial y la
 *    última semana). Con una sola, un mal fin de semana dispararía avisos.
 *  - **Datos insuficientes** no es un déficit: es no saber. Nunca se presenta
 *    como si faltara el nutriente.
 */
export function nutrientHistory(days: readonly NutrientDay[], onDate: string): HistoryResult {
  const valid = days
    .filter((d) => d.complete && d.ratio != null && d.coverage >= MIN_COVERAGE)
    .filter((d) => daysBetween(d.date, onDate) >= 0)
    .map((d) => ({ date: d.date, ratio: clamp(d.ratio!, 0, HISTORY.ratioCap) }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const last7 = meanOfLast(valid, HISTORY.recentWindowDays);
  const last30 = meanOfLast(valid, HISTORY.silenceWindowDays);
  const ema = exponentialMean(valid, onDate);

  return {
    status: classify(valid, days, onDate, { ema, last7, last30 }),
    ema,
    last7,
    last30,
    validDays: valid.length,
  };
}

function classify(
  valid: readonly { date: string; ratio: number }[],
  all: readonly NutrientDay[],
  onDate: string,
  means: { ema: number; last7: number; last30: number },
): HistoryStatus {
  // 1. Silencio, por delante de todo.
  const lastMonth = valid.slice(-HISTORY.silenceWindowDays);
  if (lastMonth.length >= HISTORY.minValidDaysIn7 && means.last30 >= HISTORY.silenceThreshold) {
    return 'silencio';
  }

  // 2. ¿Hay material suficiente para opinar?
  const recentValid = all.filter(
    (d) =>
      d.complete &&
      d.ratio != null &&
      d.coverage >= MIN_COVERAGE &&
      daysBetween(d.date, onDate) >= 0 &&
      daysBetween(d.date, onDate) < HISTORY.recentWindowDays,
  ).length;

  if (recentValid < HISTORY.minValidDaysIn7) return 'datos_insuficientes';

  // 3. Alerta: las dos medidas por debajo del umbral.
  if (means.ema < HISTORY.alertThreshold && means.last7 < HISTORY.alertThreshold) {
    return 'alerta';
  }

  return 'vigilar';
}

function meanOfLast(values: readonly { ratio: number }[], count: number): number {
  const slice = values.slice(-count);
  if (slice.length === 0) return 0;
  return round6(slice.reduce((sum, v) => sum + v.ratio, 0) / slice.length);
}

/**
 * Media ponderada exponencial con semivida de 14 días, pesada por la distancia
 * a `onDate` y no por la posición en la lista: si alguien no registra durante
 * dos semanas, esos días no deben pesar como si fueran de ayer.
 */
function exponentialMean(
  values: readonly { date: string; ratio: number }[],
  onDate: string,
): number {
  if (values.length === 0) return 0;

  const perDay = decayPerDay(HISTORY.halfLifeDays);
  let weighted = 0;
  let total = 0;

  for (const { date, ratio } of values) {
    const age = Math.max(daysBetween(date, onDate), 0);
    const weight = Math.pow(perDay, age);
    weighted += ratio * weight;
    total += weight;
  }

  return total === 0 ? 0 : round6(weighted / total);
}
